// Decides whether a failed daily locale lookup is expected (only logged) or must be reported.
// Kept out of location.service.ts so it stays loadable under plain Node for its unit test:
// location.service.ts imports @angular/core and the settings service chain.

import { CloudFunctionNetworkError, wasReportedByCloudFunctions } from './cloud-functions-errors';
import { isTransientFetchError } from './error-patterns';

/** Whether a rejection from `getLocaleAsync` needs no report of its own. Covers a Safari-backgrounding
 * AbortError, the native connectivity-loss families (iOS "The network connection was lost." — Sentry
 * triage v1.25.0 Group 11, issue 3XX — and the Android socket shapes), and every failure the
 * cloud-functions layer already reported, or deliberately left unreported because the phone was offline
 * (#6233, df-e9009a88832f). Anything else reached the caller with nobody having reported it. */
export function isLocaleFetchFailureExpected(error: unknown): boolean {
  // if the CF wrapper gave up after its retries, it reported the failure and wrapped it
  if (error instanceof CloudFunctionNetworkError) return true;
  // if Safari aborted the request while backgrounding the app, nothing went wrong
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  // if a native connectivity-loss shape arrives raw (thrown outside the wrapper), it is expected
  if (isTransientFetchError(error)) return true;
  // if callApiAsync reported an unrecognised fetch rejection (or skipped it while offline),
  // or callable() reported a 502/503 after its retry, a second report would be a duplicate
  return wasReportedByCloudFunctions(error);
}
